import {useState} from 'react'
import {useQuery} from '@apollo/client'
import {PERMISSION_CATALOG, ROLES, PERMISSION_GROUPS} from '../../../gql/authz'
import {PermissionEditor} from './permissions/PermissionEditor'
import {BulkAssignPanel} from './permissions/BulkAssignPanel'
import {RolesManager} from './permissions/RolesManager'
import {defineMessages, FormattedMessage, MessageDescriptor, useIntl} from 'react-intl'

const M = defineMessages({
  title: {id: 'admin.permissions.title', defaultMessage: 'Roles & Permissions'},
  subtitle: {
    id: 'admin.permissions.subtitle',
    defaultMessage:
      'Manage roles, edit permissions by role / group / user, or bulk-assign. Changes apply on the next permission refresh.',
  },
  modeRoles: {id: 'admin.permissions.mode.roles', defaultMessage: 'Roles'},
  modeEdit: {id: 'admin.permissions.mode.edit', defaultMessage: 'Edit permissions'},
  modeBulk: {id: 'admin.permissions.mode.bulk', defaultMessage: 'Bulk assign'},
})

type Mode = 'roles' | 'edit' | 'bulk'
type SubjectType = 'role' | 'group' | 'user'

// Permissions center (Users → Permissions): manage roles, edit permissions for a
// role/group/user, or bulk-assign.
export const PermissionsMatrixTab = () => {
  const intl = useIntl()
  const [mode, setMode] = useState<Mode>('roles')
  const [editorInitial, setEditorInitial] = useState<{type: SubjectType; id: string} | undefined>()

  const {data: cat} = useQuery(PERMISSION_CATALOG, {fetchPolicy: 'cache-and-network'})
  const {data: rolesData, refetch: refetchRoles} = useQuery(ROLES, {fetchPolicy: 'cache-and-network'})
  const {data: groupsData, refetch: refetchGroups} = useQuery(PERMISSION_GROUPS, {fetchPolicy: 'cache-and-network'})

  const modules = cat?.permissionCatalog?.modules ?? []
  const roles = rolesData?.roles ?? []
  const groups = groupsData?.permissionGroups ?? []

  // Vive fuera del render, asi que guarda el descriptor y se formatea al pintar.
  const modes: {key: Mode; label: MessageDescriptor}[] = [
    {key: 'roles', label: M.modeRoles},
    {key: 'edit', label: M.modeEdit},
    {key: 'bulk', label: M.modeBulk},
  ]

  const goMode = (m: Mode) => {
    if (m === 'edit') setEditorInitial(undefined) // direct tab → default (Users)
    setMode(m)
  }

  return (
    <div>
      <div className='mb-3'>
        <h3 className='mb-1'>
          <FormattedMessage {...M.title} />
        </h3>
        <span className='text-muted fs-7'>
          <FormattedMessage {...M.subtitle} />
        </span>
      </div>

      <ul className='nav nav-tabs nav-line-tabs fs-6 mb-4'>
        {modes.map((m) => (
          <li className='nav-item' key={m.key}>
            <a
              className={`nav-link ${mode === m.key ? 'active' : ''}`}
              style={{cursor: 'pointer'}}
              onClick={() => goMode(m.key)}
            >
              {intl.formatMessage(m.label)}
            </a>
          </li>
        ))}
      </ul>

      {mode === 'roles' && (
        <RolesManager
          onEditRole={(id) => {
            setEditorInitial({type: 'role', id})
            setMode('edit')
          }}
        />
      )}

      {mode === 'edit' && (
        <PermissionEditor
          key={editorInitial ? `${editorInitial.type}-${editorInitial.id}` : 'default'}
          initialSubject={editorInitial}
        />
      )}

      {mode === 'bulk' && (
        <BulkAssignPanel
          modules={modules}
          roles={roles}
          groups={groups}
          onDone={() => {
            refetchRoles()
            refetchGroups()
          }}
        />
      )}
    </div>
  )
}
